// src/utils/cleanTransactionName.js
/**
 * Limpa o nome da transação removendo prefixos e sufixos bancários
 * Aplicado apenas para o banco "COOP DE CRED, POUP E INV C GER"
 */

// Padrões de prefixos e sufixos a serem removidos
const BANK_PATTERNS = {
  // Prefixos bancários (para outros bancos)
  prefixes: [
    /^sicoob[_\s]*.*?pix[_\s]*/i,
    /^stone[_\s]*/i,
    /^(pag|pagamento)[_\s]*/i,
    /^(pix|ted|doc)[_\s]*/i,
    /^(deb|débito|debit|crédito|credit)[_\s]*/i,
    /^(retirada|saque)[_\s]*/i,
    /^(transferência|transfer)[_\s]*/i,
    /^(liquidação|boleto)[_\s]*/i,
    /^(\w+\s+)+(deb|cr|pix)\s+/i
  ],
    
  // Sufixos bancários
  suffixes: [
    /\s*[-|]?\s*(débito|debito|crédito|credito|debit|credit)(\s*[-|]\s*\w+)*$/i,
    /\s*[-|]\s*(físico|online|digital|maquininha)$/i,
    /\s+(deb|cr|pix|ted|doc|antec)\s*$/i,
    /\s*\|.*$/i
  ],
    
  // Padrões intermediários para remover
  intermediate: [
    /\s+(pix|pag|pagamento)\s+/gi,
    /\s+(deb|débito)\s+/gi,
    /\s+(cr|crédito)\s+/gi,
    /\s+(antec)\s+/gi,
    /\s+sub\s+/gi
  ]
};

/**
 * Limpa o nome da transação removendo prefixos e sufixos bancários
 * @param {string} description - Descrição da transação
 * @param {string} bankName - Nome do banco para referência
 * @returns {string} - Descrição limpa
 */
export const cleanTransactionName = (description, bankName = '') => {
  if (!description) return '';
  
  // Retorna a descrição original para qualquer banco que não seja "COOP DE CRED, POUP E INV C GER"
  if (bankName !== 'COOP DE CRED, POUP E INV C GER') {
    return description.trim();
  }
  
  let cleaned = description.trim();
  
  // Tratar o caso específico de "INTEGR.CAPITAL SUBSCRITO-1"
  if (cleaned.includes('CAPITAL SUBSCRITO')) {
    return 'INTEGRALIZACAO DE CAPITAL';
  }
  
  // Tratar casos específicos por tipo de transação
  
  // 1. Compras com cartão
  if (cleaned.toUpperCase().includes('COMPRAS NACIONAIS')) {
    const match = cleaned.match(/COMPRAS NACIONAIS-[A-Z0-9]+\s+(.*)/i);
    if (match && match[1]) {
      return match[1].trim();
    }
  }
  
  // 2. Boletos
  else if (cleaned.toUpperCase().includes('LIQUIDACAO BOLETO')) {
    // Remover CPF/CNPJ e manter apenas o nome da empresa
    const match = cleaned.match(/LIQUIDACAO BOLETO-\s*\d+\s+(.*)/i);
    if (match && match[1]) {
      return match[1].trim();
    }
    
    // Segunda tentativa: remover apenas o prefixo
    return cleaned.replace(/^LIQUIDACAO BOLETO-\s*/i, '').trim();
  }
  
  // 3. Pagamentos PIX
  else if (cleaned.toUpperCase().includes('PAGAMENTO PIX')) {
    if (cleaned.includes('PIX_DEB')) {
      // Formato "PAGAMENTO PIX-PIX_DEB 36958572000125 C2 CAFETERIA LTDA"
      const match = cleaned.match(/PAGAMENTO PIX-PIX_DEB\s+\d+\s+(.*)/i);
      if (match && match[1]) {
        return match[1].trim();
      }
    } else if (cleaned.toUpperCase().includes('SICREDI')) {
      // Formato "PAGAMENTO PIX SICREDI-CX695198 16828505000189 MOKA..."
      const match = cleaned.match(/PAGAMENTO PIX SICREDI-[A-Z0-9]+\s+\d+\s+(.*)/i);
      if (match && match[1]) {
        return match[1].trim();
      }
    }
    
    // Padrão geral para PIX
    const pixMatch = cleaned.match(/PAGAMENTO PIX.*?(\d{8,})\s+(.*)/i);
    if (pixMatch && pixMatch[2]) {
      return pixMatch[2].trim();
    }
  }
  
  // 4. Para outros casos, aplicar limpeza genérica COOP
  // Remover prefixos comuns
  cleaned = cleaned.replace(/^(COMPRAS NACIONAIS|LIQUIDACAO BOLETO|PAGAMENTO PIX|INTEGR\.CAPITAL)[^a-zA-Z]*/i, '');
  
  // Remover códigos de referência
  cleaned = cleaned.replace(/-[A-Z0-9]+\s+/i, ' ');
  cleaned = cleaned.replace(/\s*(VE|CX|DB|CR|PIX_DEB)[0-9]+\s*/gi, ' ');
  
  // Remover CNPJs e sequências de dígitos (mantendo números isolados como parte do nome)
  cleaned = cleaned.replace(/\d{8,}\s+/g, '');
  
  // Remove espaços extras
  cleaned = cleaned.replace(/\s+/g, ' ').trim();
  
  // Remove números isolados no início ou fim
  cleaned = cleaned.replace(/^\d+\s*|\s*\d+$/g, '');
  
  // Remove caracteres especiais no início ou fim
  cleaned = cleaned.replace(/^[^\w\s]+|[^\w\s]+$/g, '').trim();
  
  // Para transações do tipo "prefixo - o que se quer manter 123 com numeros 456"
  if (cleaned.includes('-')) {
    const parts = cleaned.split('-');
    if (parts.length > 1) {
      // Etapa 1: Pega a parte depois do primeiro "-" (e junta o restante se houver mais hífens)
      cleaned = parts.slice(1).join('-').trim();

      // Etapa 2: Remove todos os conjuntos de dígitos da string resultante
      cleaned = cleaned.replace(/\d+/g, ''); // Remove qualquer sequência de um ou more dígitos

      // Etapa 3: Limpa espaços em branco extras que podem ter surgido após a remoção dos números
      // e garante que não haja espaços no início ou no fim.
      cleaned = cleaned.replace(/\s+/g, ' ').trim();
    }
  }
  
  // Remove qualquer texto restante após separadores
  cleaned = cleaned.replace(/\s*[-|]\s*.*$/, '');
  
  // Limpeza final
  cleaned = cleaned.trim();
  
  return cleaned || description.trim(); // Fallback para a descrição original se a limpeza resultar em string vazia
};

/**
 * Cria uma chave normalizada para categorização considerando apenas o nome limpo
 * @param {string} description - Descrição da transação
 * @param {string} bankName - Nome do banco
 * @returns {string} - Chave normalizada
 */
export const getCategorizationKey = (description, bankName = '') => {
  // Apenas normaliza para o banco específico
  if (bankName === 'COOP DE CRED, POUP E INV C GER') {
    const cleaned = cleanTransactionName(description, bankName);
    return cleaned.toLowerCase().trim();
  }
  
  // Para outros bancos, retorna a descrição original em lowercase
  return description.toLowerCase().trim();
};